import { apiClient, type PaginatedResponse } from '../../../api/client'
import buildApiUrl, { buildFetchUrl, buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import { type PaginatedQueryParameters, type GenericFilterType } from '../../../api/hooks/utils/types'
import { type EntitySearchResponse } from '../../../hooks/useEntitySearch'
import { APIArticleFromJSON } from '../../../generated-types/models/APIArticle'
import {
  APIFeedbackForm,
  APIFeedbackFormFromJSON
} from "../../../generated-types/models/APIFeedbackForm";
import {
  type APIFeedbackSubmission,
  APIFeedbackSubmissionFromJSON,
} from '../../../generated-types/models/APIFeedbackSubmission'
import { APIPropertyFromJSON } from '../../../generated-types/models/APIProperty'
import { APICategoryFromJSON } from '../../../generated-types/models/APICategory'
import { APITypeFromJSON } from '../../../generated-types/models/APIType'
import { APIFamilyFromJSON } from '../../../generated-types/models/APIFamily'
import { APIMaterialFromJSON } from '../../../generated-types/models/APIMaterial'
import {
  type APISharepoint,
  APISharepointFromJSON,
} from '../../../generated-types/models/APISharepoint'
import {
  type APISharepointCreationRequest,
} from '../../../generated-types/models/APISharepointCreationRequest'
import { ItemsarticlesApi } from '../../../generated-types/apis/ItemsarticlesApi'
import { ItemssharepointApi } from '../../../generated-types/apis/ItemssharepointApi'
import { ItemsfeedbackFormApi } from '../../../generated-types/apis/ItemsfeedbackFormApi'
import { ItemsfeedbackSubmissionApi } from '../../../generated-types/apis/ItemsfeedbackSubmissionApi'
import { ItemscategoriesApi } from '../../../generated-types/apis/ItemscategoriesApi'
import { ItemspropertiesApi } from '../../../generated-types/apis/ItemspropertiesApi'
import { ItemstypesApi } from '../../../generated-types/apis/ItemstypesApi'
import { ItemsfamiliesApi } from '../../../generated-types/apis/ItemsfamiliesApi'
import { ItemsmaterialsApi } from '../../../generated-types/apis/ItemsmaterialsApi'
import { Configuration } from '../../../generated-types/runtime'
import { getAccessToken } from '../../../api/authTokens'
import Cookies from 'js-cookie'
import LogTool from '../../../logger/logTools'
import {
  createFileInfo,
  createGuestUser,
  createUser,
  getPKfromSelf,
  parseNestedObject,
  parseNestedObjectList,
} from '../../../utils/functions'
import { createRequest } from '../../Request/api/requestApi'
import { createSupplier, createSupplierApi, formInputToAPISupplierJSON } from '../../Supplier/api/supplierApi'
import { createGuestRequest } from '../../UnauthRequest/utils/functions'
import {
  type Article,
  type ArticleFormInput,
  type FeedbackForm,
  type FeedbackSubmission,
  type Property,
  type Type,
  type Family,
  type Material,
  type Sharepoint,
} from '../utils/types'
import { type Category, type Supplier } from '../../Supplier/utils/types'
import { APIFeedbackFormRequest } from '../../../generated-types/models/APIFeedbackFormRequest'

const log = new LogTool({ context: 'ArticleAPI', enable: true, logLevel: 'warn' })

/**
 * Filter types for Article queries
 */
export interface ArticleFilters extends GenericFilterType {
  article_number?: string
  name?: string
  category?: string
  supplier?: string
  brand?: string
  family?: string
  type?: string
  description?: string
  status?: string
  created_at?: string
  last_modified_at?: string
}

/**
 * Get API configuration with authentication token
 */
function getApiConfiguration(): Configuration {
  const envApiUrl = process.env.REACT_APP_API_URL
  if (!envApiUrl) {
    throw new Error('API URL is not defined. Please set REACT_APP_API_URL environment variable.')
  }
  
  return new Configuration({
    basePath: envApiUrl.endsWith('/') ? envApiUrl.slice(0, -1) : envApiUrl,
    accessToken: async () => {
      // Try to fetch an existing token; refresh automatically if missing/expired
      const token = await getAccessToken()
      if (!token) {
        log.warn('No access token available. API request may fail with 401.')
        return ''
      }
      return token
    }
  })
}

/**
 * Create API instances
 */
function getArticlesApi(): ItemsarticlesApi {
  return new ItemsarticlesApi(getApiConfiguration())
}

function getSharepointsApi(): ItemssharepointApi {
  return new ItemssharepointApi(getApiConfiguration())
}

function getFeedbackFormsApi(): ItemsfeedbackFormApi {
  return new ItemsfeedbackFormApi(getApiConfiguration())
}

function getFeedbackSubmissionsApi(): ItemsfeedbackSubmissionApi {
  return new ItemsfeedbackSubmissionApi(getApiConfiguration())
}

function getCategoriesApi(): ItemscategoriesApi {
  return new ItemscategoriesApi(getApiConfiguration())
}

function getPropertiesApi(): ItemspropertiesApi {
  return new ItemspropertiesApi(getApiConfiguration())
}

function getTypesApi(): ItemstypesApi {
  return new ItemstypesApi(getApiConfiguration())
}

function getFamiliesApi(): ItemsfamiliesApi {
  return new ItemsfamiliesApi(getApiConfiguration())
}

function getMaterialsApi(): ItemsmaterialsApi {
  return new ItemsmaterialsApi(getApiConfiguration())
}

type LeadTimeResponse = {
  months?: number | string
  weeks?: number | string
  days?: number | string
}

type MaybeExpanded<T> = T | string | null | undefined
type MaybeExpandedList<T> = Array<T | string> | string | null | undefined

interface ArticleResponseRaw extends Record<string, unknown> {
  lead_time?: LeadTimeResponse | string | null
  supplier?: MaybeExpanded<Record<string, unknown>>
  material?: MaybeExpanded<Record<string, unknown>>
  guest_request?: MaybeExpanded<Record<string, unknown>>
  request?: MaybeExpanded<Record<string, unknown>>
  requests?: MaybeExpandedList<Record<string, unknown>>
  files?: MaybeExpandedList<Record<string, unknown>>
  properties?: MaybeExpandedList<Record<string, unknown>>
}

type ArticlePropertiesResponse = ArticleResponseRaw['properties']

type ArticleMutationInput = Partial<ArticleFormInput & Article> & Record<string, unknown>
type SharepointMutationInput = Record<string, unknown>
type FeedbackFormMutationInput = Record<string, unknown>
type FeedbackSubmissionInput = Record<string, unknown>

interface FeedbackFormResponse extends Record<string, unknown> {
  form_json?: unknown
}

interface FeedbackSubmissionResponse extends Record<string, unknown> {
  article?: unknown
  submitted_by?: unknown
}

interface SharepointResponseRaw extends Record<string, unknown> {
  created_by?: unknown
  feedback_form?: unknown
  shared_articles?: MaybeExpandedList<Record<string, unknown>>
  shared_boms?: MaybeExpandedList<Record<string, unknown>>
  shared_guest_request?: MaybeExpanded<Record<string, unknown>>
  shared_request?: MaybeExpanded<Record<string, unknown>>
  shared_with?: MaybeExpandedList<Record<string, unknown>>
  feedback_submissions?: MaybeExpandedList<Record<string, unknown>>
  unread_feedback_submission_count?: number
}

const LEAD_TIME_KEYS: Array<keyof Required<LeadTimeResponse>> = ['months', 'weeks', 'days']

function isLeadTimeResponse(value: unknown): value is LeadTimeResponse {
  if (typeof value !== 'object' || value === null) {
    return false
  }

  const record = value as Record<string, unknown>
  return LEAD_TIME_KEYS.every(key => {
    const field = record[key]
    return (
      field === undefined ||
      field === null ||
      typeof field === 'number' ||
      typeof field === 'string'
    )
  })
}

function parseArticleProperties(properties: ArticlePropertiesResponse): Article['properties'] {
  if (!properties) {
    return []
  }

  if (Array.isArray(properties)) {
    if (properties.every(property => typeof property === 'object' && property !== null)) {
      return properties.map(property => {
        const apiProperty = APIPropertyFromJSON(property)
        return {
          ...apiProperty,
          key: getPKfromSelf(apiProperty.self),
        } as Property
      })
    }

    if (properties.every(property => typeof property === 'string')) {
      return properties
    }
  }

  if (typeof properties === 'string') {
    return [properties]
  }

  return []
}

function extractIdentifier(value: unknown): string | undefined {
  if (typeof value === 'string') {
    // Only return string if it looks like a URL (starts with http://, https://, or /v1/)
    // This prevents sending plain text names instead of URLs
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/v1/') || value.startsWith('v1/')) {
      return value
    }
    // If it's not a URL, return undefined to avoid sending invalid data
    return undefined
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>

    if (typeof record.self === 'string') {
      return record.self
    }

    if ('value' in record) {
      const nestedValue = record.value
      if (typeof nestedValue === 'string') {
        // Only return if it's a URL
        if (nestedValue.startsWith('http://') || nestedValue.startsWith('https://') || nestedValue.startsWith('/v1/') || nestedValue.startsWith('v1/')) {
          return nestedValue
        }
      }
      if (nestedValue && typeof nestedValue === 'object') {
        const nestedRecord = nestedValue as Record<string, unknown>
        if (typeof nestedRecord.self === 'string') {
          return nestedRecord.self
        }
      }
    }
  }

  return undefined
}

function extractIdentifiers(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map(item => extractIdentifier(item))
      .filter((identifier): identifier is string => typeof identifier === 'string' && identifier.length > 0)
  }

  const result = extractIdentifier(value)
  return result ? [result] : []
}

/**
 * Parse article leadTime from API response
 */
function parseLeadTime(leadTime: unknown): { months: string; weeks: string; days: string } | null {
  if (!leadTime || typeof leadTime === 'string') {
    return null
  }
  if (!isLeadTimeResponse(leadTime)) {
    return null
  }

  return {
    months: String(leadTime.months ?? '0'),
    weeks: String(leadTime.weeks ?? '0'),
    days: String(leadTime.days ?? '0'),
  }
}

/**
 * Create Article from API response
 */
export function createArticle(articleResponse: unknown): Article {
  const normalizedResponse =
    typeof articleResponse === 'object' && articleResponse !== null
      ? articleResponse as ArticleResponseRaw
      : {} as ArticleResponseRaw

  const apiArticle = APIArticleFromJSON(normalizedResponse)
  const filesSource = normalizedResponse.files
  const propertiesSource = (normalizedResponse.properties ?? apiArticle.properties) as ArticlePropertiesResponse
  const supplierParsed = parseNestedObject(apiArticle.supplier, createSupplier)
  const supplier =
    supplierParsed === null || typeof supplierParsed === 'string'
      ? null
      : supplierParsed
  // Preserve material object from raw response if it's expanded (not just a string hyperlink)
  // APIArticleFromJSON converts material to string based on type, but we need the object when expanded
  const materialSource = normalizedResponse.material
  const materialParsed = 
    materialSource && typeof materialSource === 'object' && !Array.isArray(materialSource)
      ? parseNestedObject(materialSource, createMaterial)
      : null

  const article: Article = {
    ...apiArticle,
    key: getPKfromSelf(apiArticle.self),
    type: apiArticle.articleType ?? undefined,
    leadTime: parseLeadTime(normalizedResponse.lead_time ?? apiArticle.leadTime),
    supplier,
    // @ts-expect-error - Material can be an object when expanded, but Article type only allows string
    // This is handled at runtime in articleToPartInfoFormState which checks typeof material === 'object'
    material: materialParsed ?? apiArticle.material,
    guestRequest: parseNestedObject(normalizedResponse.guest_request, createGuestRequest),
    request: parseNestedObject(normalizedResponse.request, createRequest),
    requests: parseNestedObjectList(normalizedResponse.requests, createRequest),
    files: filesSource === undefined ? [] : parseNestedObjectList(filesSource, createFileInfo),
    properties: parseArticleProperties(propertiesSource),
    // Map last_modified_at from raw response to lastModifiedAt (camelCase)
    // Use apiArticle.lastModifiedAt as fallback since APIArticle always has it
    lastModifiedAt: normalizedResponse.last_modified_at 
      ? new Date(normalizedResponse.last_modified_at as string)
      : apiArticle.lastModifiedAt
  }
  return article
}

/**
 * Create FeedbackForm from API response
 */
export function createFeedbackForm(response: unknown): FeedbackForm {
  const normalizedResponse =
    typeof response === 'object' && response !== null
      ? (response as FeedbackFormResponse)
      : ({} as FeedbackFormResponse)

  // If the API client already converted to camelCase (formJson), skip the JSON parser.
  const isAlreadyParsed =
    'formJson' in normalizedResponse && !('form_json' in normalizedResponse)

  const apiFeedbackForm: APIFeedbackForm = isAlreadyParsed
    ? (normalizedResponse as unknown as APIFeedbackForm)
    : APIFeedbackFormFromJSON(normalizedResponse)
  
  // Handle formJson: it can be either a string (JSON), already an array, or undefined
  let formJson: any[] = []
  
  // First check the raw response (form_json from API)
  if (typeof normalizedResponse.form_json === 'string') {
    try {
      formJson = JSON.parse(normalizedResponse.form_json)
    } catch (e) {
      log.warn('Error parsing form_json as string:', e)
      formJson = []
    }
  } else if (Array.isArray(normalizedResponse.form_json)) {
    formJson = normalizedResponse.form_json
  } else if (apiFeedbackForm.formJson) {
    // If APIFeedbackFormFromJSON already parsed it, use that (formJson is camelCase in the type)
    if (Array.isArray(apiFeedbackForm.formJson)) {
      formJson = apiFeedbackForm.formJson
    } else if (typeof apiFeedbackForm.formJson === 'string') {
      try {
        formJson = JSON.parse(apiFeedbackForm.formJson)
      } catch (e) {
        log.warn('Error parsing formJson from APIFeedbackForm:', e)
        formJson = []
      }
    }
  }
  
  // Normalize fields so the Form renderer can understand them
  const normalizedFields = Array.isArray(formJson)
    ? formJson.map((formField: any) => {
        const normalizedField = {
          ...formField,
          // Backend uses formFieldType because "type" is reserved; map it back
          type: formField.formFieldType ?? formField.type,
          // Keep the chosen column width across breakpoints
          sm: formField.xl,
          md: formField.xl,
          lg: formField.xl,
        }
        delete normalizedField.formFieldType
        return normalizedField
      })
    : []

  return {
    ...apiFeedbackForm,
    key: getPKfromSelf(apiFeedbackForm.self),
    formJson: normalizedFields,
  }
}

/**
 * Create FeedbackSubmission from API response
 */
export function createFeedbackSubmission(response: unknown): FeedbackSubmission {
  const normalizedResponse =
    typeof response === 'object' && response !== null
      ? response as FeedbackSubmissionResponse
      : {} as FeedbackSubmissionResponse

  const apiFeedbackSubmission = APIFeedbackSubmissionFromJSON(normalizedResponse)
  return {
    ...apiFeedbackSubmission,
    key: getPKfromSelf(apiFeedbackSubmission.self),
    article: parseNestedObject(normalizedResponse.article, createArticle),
    submittedBy: createGuestUser(normalizedResponse.submitted_by),
  }
}

/**
 * Create Sharepoint from API response
 */
export function createSharepoint(response: unknown): Sharepoint {
  const normalizedResponse =
    typeof response === 'object' && response !== null
      ? response as SharepointResponseRaw
      : {} as SharepointResponseRaw

  const apiSharepoint = APISharepointFromJSON(normalizedResponse)
  const sharedArticlesRaw = parseNestedObjectList(normalizedResponse.shared_articles, createArticle)
  const sharedBOMsRaw = parseNestedObjectList(normalizedResponse.shared_boms, createArticle)
  const sharedWithRaw = parseNestedObjectList(normalizedResponse.shared_with, createGuestUser)
  const feedbackSubmissionsRaw = parseNestedObjectList(
    normalizedResponse.feedback_submissions,
    createFeedbackSubmission
  )

  return {
    ...apiSharepoint,
    key: getPKfromSelf(apiSharepoint.self),
    createdBy: parseNestedObject(normalizedResponse.created_by, createUser),
    feedbackForm: parseNestedObject(normalizedResponse.feedback_form, createFeedbackForm),
    sharedArticles: Array.isArray(sharedArticlesRaw) ? sharedArticlesRaw : [],
    sharedBOMs: Array.isArray(sharedBOMsRaw) ? sharedBOMsRaw : [],
    sharedGuestRequest: parseNestedObject(
      normalizedResponse.shared_guest_request,
      createGuestRequest
    ) as Sharepoint['sharedGuestRequest'],
    sharedRequest: parseNestedObject(normalizedResponse.shared_request, createRequest) as Sharepoint['sharedRequest'],
    sharedWith: Array.isArray(sharedWithRaw) ? sharedWithRaw : [],
    feedbackSubmissions: Array.isArray(feedbackSubmissionsRaw) ? feedbackSubmissionsRaw : [],
    unreadFeedbackSubmissionCount: normalizedResponse.unread_feedback_submission_count,
  }
}

/**
 * Convert Article form input to API JSON
 */
export function inputToAPIArticleJSON(input: ArticleMutationInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  if (typeof input.number === 'string' && input.number.length > 0) {
    payload.number = input.number
  }

  if (typeof input.name === 'string' && input.name.length > 0) {
    payload.name = input.name
  }

  if (typeof input.description === 'string' && input.description.length > 0) {
    payload.description = input.description
  }

  if (typeof input.articleType === 'string' && input.articleType.length > 0) {
    payload.article_type = input.articleType
  } else if (typeof input.type === 'string' && input.type.length > 0) {
    payload.article_type = input.type
  }

  if (typeof input.family === 'string' && input.family.length > 0) {
    payload.family = input.family
  }

  // Material - CreateSlugRelatedField with slug_field="name", expects material name as string
  if (typeof input.material === 'string' && input.material.length > 0) {
    payload.material = input.material
  } else if (input.material && typeof input.material === 'object') {
    // Handle case where material might be stored as SelectOption {value, label} or object with name/label
    const materialRecord = input.material as Record<string, unknown>
    if ('label' in materialRecord && typeof materialRecord.label === 'string' && materialRecord.label.length > 0) {
      payload.material = materialRecord.label
    } else if ('name' in materialRecord && typeof materialRecord.name === 'string' && materialRecord.name.length > 0) {
      payload.material = materialRecord.name
    }
  }

  // Supplier
  const supplierValue = input.supplier
  if (supplierValue !== undefined) {
    if (supplierValue === null || (typeof supplierValue === 'string' && supplierValue === '')) {
      payload.supplier = null
    } else {
      const supplierIdentifier = extractIdentifier(supplierValue)
      if (supplierIdentifier) {
        payload.supplier = supplierIdentifier
      }
    }
  }

  // Lead Time
  const leadTime = input.leadTime
  if (leadTime && typeof leadTime === 'object') {
    const months = Number.parseInt(String((leadTime as Record<string, unknown>).months ?? '0'), 10)
    const weeks = Number.parseInt(String((leadTime as Record<string, unknown>).weeks ?? '0'), 10)
    const days = Number.parseInt(String((leadTime as Record<string, unknown>).days ?? '0'), 10)

    payload.lead_time = {
      months: Number.isNaN(months) ? 0 : months,
      weeks: Number.isNaN(weeks) ? 0 : weeks,
      days: Number.isNaN(days) ? 0 : days,
    }
  }

  // Properties
  if (input.properties !== undefined) {
    const properties = extractIdentifiers(input.properties)
    if (properties.length > 0) {
      payload.properties = properties
    }
  }

  // Categories
  if (input.category !== undefined) {
    const categories = extractIdentifiers(input.category)
    if (categories.length > 0) {
      payload.category = categories
    }
  }

  // Barcode
  if (typeof input.barcodeText === 'string' && input.barcodeText.length > 0) {
    payload.barcode_text = input.barcodeText
  }

  if (input.barcodeType !== undefined) {
    payload.barcode_type = input.barcodeType
  }

  // Brand
  if (typeof input.brand === 'string' && input.brand.length > 0) {
    payload.brand = input.brand
  }

  // Variation Of
  const variationOfValue = input.variationOf
  if (variationOfValue !== undefined && variationOfValue !== null && variationOfValue !== '') {
    const variationOfIdentifier = extractIdentifier(variationOfValue)
    if (variationOfIdentifier) {
      payload.variation_of = variationOfIdentifier
    }
  }

  // Quality Class
  const qualityClassValue = input.qualityClass
  if (qualityClassValue !== undefined && qualityClassValue !== null && qualityClassValue !== '') {
    const qualityClassIdentifier = extractIdentifier(qualityClassValue)
    if (qualityClassIdentifier) {
      payload.quality_class = qualityClassIdentifier
    }
  }

  // Country of Origin
  const countryOfOriginValue = input.countryOfOrigin
  if (countryOfOriginValue !== undefined && countryOfOriginValue !== null && countryOfOriginValue !== '') {
    const countryOfOriginIdentifier = extractIdentifier(countryOfOriginValue)
    if (countryOfOriginIdentifier) {
      payload.country_of_origin = countryOfOriginIdentifier
    }
  }

  // Dimensions with units
  if (input.weight !== undefined && input.weight !== null) {
    // Handle both APIMeasurement object and number/string values
    if (typeof input.weight === 'object' && 'value' in input.weight) {
      // Already an APIMeasurement object
      payload.weight = input.weight
    } else if (typeof input.weight === 'number' || (typeof input.weight === 'string' && input.weight !== '')) {
      const weightValue = typeof input.weight === 'number' ? input.weight : Number.parseFloat(input.weight)
      if (!Number.isNaN(weightValue)) {
        payload.weight = {
          value: weightValue,
          unit: input.weightUnit || 'kg'
        }
      }
    }
  }

  if (input.length !== undefined && input.length !== null) {
    if (typeof input.length === 'object' && 'value' in input.length) {
      payload.length = input.length
    } else if (typeof input.length === 'number' || (typeof input.length === 'string' && input.length !== '')) {
      const lengthValue = typeof input.length === 'number' ? input.length : Number.parseFloat(input.length)
      if (!Number.isNaN(lengthValue)) {
        payload.length = {
          value: lengthValue,
          unit: input.lengthUnit || 'm'
        }
      }
    }
  }

  if (input.width !== undefined && input.width !== null) {
    if (typeof input.width === 'object' && 'value' in input.width) {
      payload.width = input.width
    } else if (typeof input.width === 'number' || (typeof input.width === 'string' && input.width !== '')) {
      const widthValue = typeof input.width === 'number' ? input.width : Number.parseFloat(input.width)
      if (!Number.isNaN(widthValue)) {
        payload.width = {
          value: widthValue,
          unit: input.widthUnit || 'm'
        }
      }
    }
  }

  if (input.height !== undefined && input.height !== null) {
    if (typeof input.height === 'object' && 'value' in input.height) {
      payload.height = input.height
    } else if (typeof input.height === 'number' || (typeof input.height === 'string' && input.height !== '')) {
      const heightValue = typeof input.height === 'number' ? input.height : Number.parseFloat(input.height)
      if (!Number.isNaN(heightValue)) {
        payload.height = {
          value: heightValue,
          unit: input.heightUnit || 'm'
        }
      }
    }
  }

  if (input.volume !== undefined && input.volume !== null) {
    if (typeof input.volume === 'object' && 'value' in input.volume) {
      payload.volume = input.volume
    } else if (typeof input.volume === 'number' || (typeof input.volume === 'string' && input.volume !== '')) {
      const volumeValue = typeof input.volume === 'number' ? input.volume : Number.parseFloat(input.volume)
      if (!Number.isNaN(volumeValue)) {
        payload.volume = {
          value: volumeValue,
          unit: input.volumeUnit || 'cubic_meter'
        }
      }
    }
  }


  // Version Comment
  if (typeof input.versionComment === 'string' && input.versionComment.length > 0) {
    payload.version_comment = input.versionComment
  }

  // Guest Request and Shared Request
  if (input.guestRequest !== undefined) {
    payload.guest_request = input.guestRequest
  }

  if (input.sharedRequest !== undefined) {
    payload.shared_request = input.sharedRequest
  }

  return payload
}

export function inputToAPIFeedbackFormJSON(input: any): APIFeedbackFormRequest {
  const fallbackTitle =
    typeof input?.formTitle === 'string' && input.formTitle.trim().length > 0
      ? input.formTitle.trim()
      : 'Feedback Form'

  const formJsonSource: any[] = Array.isArray(input?.formJson) ? input.formJson : []
  const normalizedFields = formJsonSource.map((formField: any, index: number) => {
    const fallbackLabel =
      typeof formField?.label === 'string' && formField.label.trim().length > 0
        ? formField.label
        : `Field ${index + 1}`

    const resolvedKey =
      typeof formField?.key === 'string' && formField.key.trim().length > 0
        ? formField.key
        : fallbackLabel.replace(/\s+/g, '_').toLowerCase()

    const resolvedTypeSource = formField?.formFieldType ?? formField?.type ?? 'text'
    const resolvedType =
      typeof resolvedTypeSource === 'string' && resolvedTypeSource.trim().length > 0
        ? resolvedTypeSource
        : 'text'

    return {
      key: resolvedKey,
      label: fallbackLabel,
      formFieldType: resolvedType,
      required: Boolean(formField?.required ?? false),
      xl: typeof formField?.xl === 'number' ? formField.xl : 12,
    }
  })

  return {
    formTitle: fallbackTitle,
    formJson: normalizedFields,
  }
}

export function inputToAPIFeedbackSubmissionJSON(
  input: any,
  article: Article
): APIFeedbackSubmission {
  return {
    submission: input,
    article: article.self,
  } as APIFeedbackSubmission
}

export function inputToAPISharepointJSON(input: any): APISharepointCreationRequest {
  return {
    ...(input.feedbackForm && { feedbackForm: input.feedbackForm }),
    shareDuration:
      typeof input.shareDuration !== 'undefined' ? Number(input.shareDuration) : 0,
    ...(input.articles && { articles: input.articles }),
    ...(input.isBomShare !== undefined && { isBomShare: input.isBomShare }),
    ...(input.request && { request: input.request }),
    ...(input.guestRequest && { guestRequest: input.guestRequest }),
    ...(input.automaticShares && {
      automaticShares: input.automaticShares.map((automaticShare: any) => ({
        contact: automaticShare.contact,
        customInvitationMessage: automaticShare.customInvitationMessage ?? '',
      })),
    }),
    ...(input.manualShares && { manualShares: input.manualShares }),
  } as APISharepointCreationRequest
}

/**
 * Fetch paginated articles from API
 */
export async function fetchArticles(
  options: PaginatedQueryParameters<Article, ArticleFilters> | PaginationOptions = {},
  ids?: number[]
): Promise<PaginatedResponse<Article>> {
  log.info('Fetching articles with options:', options)

  const url = buildApiUrl('v1/items/articles/', options as PaginatedQueryParameters<Article, ArticleFilters>, ids)
  const response = await apiClient.get<PaginatedResponse<ArticleResponseRaw>>(url)

  const articles = response.results ? response.results.map(createArticle) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: articles,
  }
}

/**
 * Search articles using Elasticsearch
 */
export async function searchArticles(
  options: PaginatedQueryParameters<Article, ArticleFilters> = {}
): Promise<EntitySearchResponse<Article>> {
  // Ensure q parameter is set - use '*' (match all) if filters are present but no q is set
  const filters = options.filters || {}
  const hasFilters = Object.keys(filters).length > 0
  const hasQ = filters.q !== undefined && filters.q !== null && filters.q !== ''
  
  const searchFilters = {
    ...filters,
    ...(hasFilters && !hasQ ? { q: '*' } : {}),
  }
  
  const url = buildApiUrl(
    'v1/search/items/articles/',
    { filters: searchFilters }
  )
  return apiClient.get<EntitySearchResponse<Article>>(url)
}

/**
 * Fetch a single article by ID or URL
 * Note: Uses apiClient because it needs to support URL-based fetching and expand parameters
 */
export async function fetchArticle(
  idOrUrl: string | number,
  expand?: string[]
): Promise<Article> {
  log.info('Fetching article:', idOrUrl)

  // If it's a simple numeric ID without expand, use generated API
  if (typeof idOrUrl === 'number' && !expand) {
    const response = await getArticlesApi().v1ItemsArticlesRetrieve({ id: idOrUrl })
    return createArticle(response)
  }

  // For URLs and expand parameters, fall back to apiClient
  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/items/articles/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<ArticleResponseRaw>(fetchUrl)
  return createArticle(response)
}

/**
 * Create a new article
 */
export async function createArticleApi(articleData: ArticleMutationInput): Promise<Article> {
  log.info('Creating article:', articleData)

  const json = inputToAPIArticleJSON(articleData)
  const response = await getArticlesApi().v1ItemsArticlesCreate({
    aPIArticleRequest: json as any,
  })

  return createArticle(response)
}

/**
 * Create a new anonymized request article
 */
export async function createAnonymizedArticleApi(articleData: ArticleMutationInput): Promise<Article> {
  log.info('Creating anonymized article:', articleData)

  const json = inputToAPIArticleJSON(articleData)
  const response = await apiClient.post<ArticleResponseRaw>(
    'v1/items/articles/anonymized_article/',
    json
  )

  return createArticle(response)
}

/**
 * Update an existing article
 */
export async function updateArticleApi(
  idOrSelf: string | number,
  articleData: ArticleMutationInput
): Promise<Article> {
  log.info('Updating article:', idOrSelf, articleData)

  const json = inputToAPIArticleJSON(articleData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getArticlesApi().v1ItemsArticlesUpdate({
      id: idOrSelf,
      aPIArticleRequest: json as any,
    })
    return createArticle(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/articles/${idOrSelf}/`
  const response = await apiClient.put<ArticleResponseRaw>(url, json)
  return createArticle(response)
}

/**
 * Patch an existing article (partial update)
 */
export async function patchArticleApi(
  idOrSelf: string | number,
  articleData: ArticleMutationInput
): Promise<Article> {
  log.info('Patching article:', idOrSelf, articleData)

  const json = inputToAPIArticleJSON(articleData)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    const response = await getArticlesApi().v1ItemsArticlesPartialUpdate({
      id: idOrSelf,
      aPIPatchedArticleRequest: json as any,
    })
    return createArticle(response)
  }

  // For URL-based updates, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/articles/${idOrSelf}/`
  const response = await apiClient.patch<ArticleResponseRaw>(url, json)
  return createArticle(response)
}

/**
 * Delete an article
 */
export async function deleteArticleApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting article:', idOrSelf)

  // If it's a numeric ID, use generated API
  if (typeof idOrSelf === 'number') {
    await getArticlesApi().v1ItemsArticlesDestroy({ id: idOrSelf })
    return
  }

  // For URL-based deletes, fall back to apiClient
  const url = idOrSelf.includes('/') ? idOrSelf : `v1/items/articles/${idOrSelf}/`
  await apiClient.delete(url)
}

/**
 * Delete multiple articles
 */
export async function deleteArticlesApi(articles: Article[]): Promise<void> {
  log.info('Deleting articles:', articles.length)

  const deletePromises = articles.map(article => deleteArticleApi(article.self))
  await Promise.all(deletePromises)
}

/**
 * Fetch paginated sharepoints from API
 * Note: The generated API uses /v1/items/sharepoints/, but this function uses general/sharepoints/
 * Keeping apiClient until endpoint is confirmed
 */
export async function fetchSharepoints(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Sharepoint>> {
  log.info('Fetching sharepoints with options:', options)

  // Try using generated API first (uses /v1/items/sharepoints/)
  try {
    const response = await getSharepointsApi().v1ItemsSharepointsList({
      page: options.page,
      pageSize: options.pageSize,
    })

    const sharepoints = response.results ? response.results.map(createSharepoint) : []

    return {
      count: response.count || 0,
      next: response.next || null,
      previous: response.previous || null,
      results: sharepoints,
    }
  } catch (error) {
    // Fallback to apiClient if generated API fails
    log.warn('Generated API failed, falling back to apiClient:', error)
    const params = buildPaginationParams(options)
    const response = await apiClient.get<PaginatedResponse<SharepointResponseRaw>>(
      `v1/general/sharepoints/?${params.join('&')}`
    )

    const sharepoints = response.results.map(createSharepoint)

    return {
      ...response,
      results: sharepoints,
    }
  }
}

/**
 * Fetch a single sharepoint by ID or URL
 */
export async function fetchSharepoint(
  idOrUrl: string | number,
  expand?: string[]
): Promise<Sharepoint> {
  log.info('Fetching sharepoint:', idOrUrl)

  // If it's a simple numeric ID without expand, try using generated API
  if (typeof idOrUrl === 'number' && !expand) {
    try {
      const response = await getSharepointsApi().v1ItemsSharepointsRetrieve({ id: idOrUrl })
      return createSharepoint(response)
    } catch (error) {
      log.warn('Generated API failed, falling back to apiClient:', error)
    }
  }

  // For URLs and expand parameters, or as fallback, use apiClient
  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/general/sharepoints/${idOrUrl}/`
    : idOrUrl

  const params = expand ? [`expand=${expand.join(',')}`] : []
  const fetchUrl = params.length > 0 ? `${url}?${params.join('&')}` : url

  const response = await apiClient.get<SharepointResponseRaw>(fetchUrl)
  return createSharepoint(response)
}

/**
 * Create a new sharepoint
 */
export async function createSharepointApi(sharepointData: SharepointMutationInput): Promise<Sharepoint> {
  log.info('Creating sharepoint:', sharepointData)

  const response = await getSharepointsApi().v1ItemsSharepointsCreate({
    aPISharepointCreationRequest: sharepointData as any,
  })
  return createSharepoint(response)
}

/**
 * Update a sharepoint
 */
export async function updateSharepointApi(
  idOrSelf: string | number,
  sharepointData: SharepointMutationInput
): Promise<Sharepoint> {
  log.info('Updating sharepoint:', idOrSelf)

  // If it's a numeric ID, try using generated API
  if (typeof idOrSelf === 'number') {
    try {
      const response = await getSharepointsApi().v1ItemsSharepointsPartialUpdate({
        id: idOrSelf,
        patchedSharepointRequest: sharepointData as any,
      } as any)
      return createSharepoint(response)
    } catch (error) {
      log.warn('Generated API failed, falling back to apiClient:', error)
    }
  }

  // For URL-based updates or as fallback, use apiClient
  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/general/sharepoints/${idOrSelf}/`
    : idOrSelf

  const response = await apiClient.patch<SharepointResponseRaw>(url, sharepointData)
  return createSharepoint(response)
}

/**
 * Delete a sharepoint
 */
export async function deleteSharepointApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting sharepoint:', idOrSelf)

  // If it's a numeric ID, try using generated API
  if (typeof idOrSelf === 'number') {
    try {
      await getSharepointsApi().v1ItemsSharepointsDestroy({ id: idOrSelf })
      return
    } catch (error) {
      log.warn('Generated API failed, falling back to apiClient:', error)
    }
  }

  // For URL-based deletes or as fallback, use apiClient
  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/general/sharepoints/${idOrSelf}/`
    : idOrSelf

  await apiClient.delete(url)
}

/**
 * Fetch all feedback forms
 */
export async function fetchFeedbackForms(): Promise<FeedbackForm[]> {
  log.info('Fetching all feedback forms')

  const response = await getFeedbackFormsApi().v1ItemsFeedbackFormsList({})
  return response.results ? response.results.map(createFeedbackForm) : []
}

/**
 * Fetch feedback form
 */
export async function fetchFeedbackForm(id: string | number): Promise<FeedbackForm> {
  log.info('Fetching feedback form:', id)

  const response = await getFeedbackFormsApi().v1ItemsFeedbackFormsRetrieve({ 
    id: String(id) 
  })
  return createFeedbackForm(response)
}

/**
 * Create feedback form
 */
export async function createFeedbackFormApi(formData: FeedbackFormMutationInput): Promise<FeedbackForm> {
  log.info('Creating feedback form')

  // Check if access token is available
  const accessToken = Cookies.get('access')
  if (!accessToken) {
    log.error('No access token available. User may not be logged in.')
    throw new Error('Authentication required. Please log in and try again.')
  }

  const payload = inputToAPIFeedbackFormJSON(formData)
  log.debug('Feedback form payload:', payload)
  
  try {
    const response = await getFeedbackFormsApi().v1ItemsFeedbackFormsCreate({
      aPIFeedbackFormRequest: payload,
    })
    return createFeedbackForm(response)
  } catch (error: any) {
    log.error('Error creating feedback form:', error)
    // Re-throw with more context if it's an auth error
    if (error?.status === 401 || error?.response?.status === 401) {
      throw new Error('Authentication failed. Please log in again.')
    }
    throw error
  }
}

/**
 * Fetch feedback submissions for a sharepoint
 * Note: Uses apiClient because this is a nested resource endpoint not available in generated API
 */
export async function fetchFeedbackSubmissions(
  sharepointId: string | number
): Promise<FeedbackSubmission[]> {
  log.info('Fetching feedback submissions for sharepoint:', sharepointId)

  const response = await apiClient.get<PaginatedResponse<FeedbackSubmissionResponse>>(
    `v1/general/sharepoints/${sharepointId}/feedback_submissions/`
  )
  return response.results.map(createFeedbackSubmission)
}

/**
 * Submit feedback
 */
export async function submitFeedbackApi(submissionData: FeedbackSubmissionInput): Promise<FeedbackSubmission> {
  log.info('Submitting feedback')

  const response = await getFeedbackSubmissionsApi().v1ItemsFeedbackSubmissionsCreate({
    aPIFeedbackSubmissionRequest: submissionData as any,
  })
  return createFeedbackSubmission(response)
}

/**
 * Submit feedback via feedback form
 */
export async function submitFeedbackViaFormApi(
  feedbackFormUrl: string,
  submissionData: FeedbackSubmissionInput
): Promise<any> {
  log.info('Submitting feedback via form:', feedbackFormUrl)

  // Try to extract ID from URL and use generated API
  const idMatch = feedbackFormUrl.match(/\/feedback_forms\/([^\/]+)\/?$/)
  if (idMatch && idMatch[1]) {
    try {
      const response = await getFeedbackFormsApi().v1ItemsFeedbackFormsSubmitCreate({
        id: idMatch[1],
        aPIFeedbackSubmissionCreationRequest: submissionData as any,
      })
      return response
    } catch (error) {
      log.warn('Generated API failed, falling back to apiClient:', error)
    }
  }

  // Fallback to apiClient for dynamic URLs
  const submitUrl = feedbackFormUrl.endsWith('/') 
    ? `${feedbackFormUrl}submit/` 
    : `${feedbackFormUrl}/submit/`

  const response = await apiClient.post<any>(submitUrl, submissionData)
  return response
}

/**
 * Export articles to CSV
 */
export async function exportArticlesApi(): Promise<{ url: string }> {
  log.info('Exporting articles to CSV')

  // apiClient.get already parses the JSON response
  const response = await apiClient.get<{ url: string }>('v1/items/export_articles/')
  return response
}

/**
 * Undo article import
 */
export async function undoImportArticlesApi(): Promise<void> {
  log.info('Undoing article import')

  try {
    // Use raw API to handle empty responses
    const response = await getArticlesApi().v1ItemsUndoImportArticlesCreateRaw({})
    
    // Check if response has content before trying to parse
    // Clone the response because text() consumes the body
    const responseClone = response.raw.clone()
    const text = await responseClone.text()
    
    if (text && text.trim().length > 0) {
      // Only parse if there's actual content
      await response.value()
    } else {
      // Empty response is valid for undo import (backend returns 200 with empty body)
      log.info('Undo import completed (empty response is expected)')
    }
  } catch (error: any) {
    // If it's a JSON parse error due to empty response, that's okay
    // The backend might return 200 with empty body (204-like behavior)
    if (error?.message?.includes('JSON.parse') || error?.message?.includes('unexpected end')) {
      log.info('Undo import completed (empty response handled)')
      return
    }
    // Re-throw other errors
    throw error
  }
}

/**
 * Fetch all articles (recursively fetches all pages)
 * Note: Use with caution for large datasets
 */
export async function fetchAllArticles(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Article[]> {
  log.info('Fetching all articles')

  let allArticles: Article[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchArticles({ ...options, page })
    allArticles = [...allArticles, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allArticles.length} articles total`)
  return allArticles
}

/**
 * Fetch articles as options for select inputs (first page)
 * Returns options in the format: { value: string, label: string }
 */
export async function fetchArticleOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching article options')

  const response = await fetchArticles({ page: 1 })
  
  return response.results.map(article => ({
    value: article.self,
    label: `${article.name ?? ''} (${article.number ?? ''})`,
  }))
}

/**
 * Fetch all articles as options for select inputs (all pages)
 * Returns options in the format: { value: string, label: string }
 * Note: Use with caution for large datasets
 */
export async function fetchAllArticleOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching all article options')

  const allArticles = await fetchAllArticles()
  
  return allArticles.map(article => ({
    value: article.self,
    label: `${article.name ?? ''} (${article.number ?? ''})`,
  }))
}

/**
 * Fetch all available brands
 */
export async function fetchBrands(): Promise<string[]> {
  log.info('Fetching brands')

  const response = await apiClient.get<string[]>('v1/items/brands/')
  return response
}

/**
 * Fetch all available brands as options for select inputs
 * Returns options in the format: { label: string }
 * Note: Brand options only have labels, no values
 */
export async function fetchBrandOptions(): Promise<
  { value?: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching brand options')

  const brands = await fetchBrands()
  
  return brands.map(brand => ({
    label: brand,
  }))
}

/**
 * Create Category from API response
 */
export function createCategory(categoryResponse: unknown): Category {
  const apiCategory = APICategoryFromJSON(categoryResponse)
  return {
    ...apiCategory,
    key: getPKfromSelf(apiCategory.self),
  }
}

/**
 * Create Property from API response (exported version for external use)
 */
export function createProperty(propertyResponse: unknown): Property {
  const apiProperty = APIPropertyFromJSON(propertyResponse)
  return {
    ...apiProperty,
    key: getPKfromSelf(apiProperty.self),
  }
}

/**
 * Fetch paginated categories from API
 */
export async function fetchCategories(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Category>> {
  log.info('Fetching categories with options:', options)

  const response = await getCategoriesApi().v1ItemsCategoriesList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const categories = response.results ? response.results.map(createCategory) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: categories,
  }
}

/**
 * Fetch a single category by ID
 */
export async function fetchCategory(id: string | number): Promise<Category> {
  log.info('Fetching category:', id)

  const response = await getCategoriesApi().v1ItemsCategoriesRetrieve({ 
    id: typeof id === 'string' ? parseInt(id, 10) : id 
  })
  return createCategory(response)
}

/**
 * Fetch all categories (recursively fetches all pages)
 */
export async function fetchAllCategories(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Category[]> {
  log.info('Fetching all categories')

  let allCategories: Category[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchCategories({ ...options, page })
    allCategories = [...allCategories, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allCategories.length} categories total`)
  return allCategories
}

/**
 * Fetch paginated properties from API
 */
export async function fetchProperties(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Property>> {
  log.info('Fetching properties with options:', options)

  const response = await getPropertiesApi().v1ItemsPropertiesList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const properties = response.results ? response.results.map(createProperty) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: properties,
  }
}

/**
 * Fetch a single property by ID
 */
export async function fetchProperty(id: string | number): Promise<Property> {
  log.info('Fetching property:', id)

  const response = await getPropertiesApi().v1ItemsPropertiesRetrieve({ 
    id: typeof id === 'string' ? parseInt(id, 10) : id 
  })
  return createProperty(response)
}

/**
 * Fetch all properties (recursively fetches all pages)
 */
export async function fetchAllProperties(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Property[]> {
  log.info('Fetching all properties')

  let allProperties: Property[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchProperties({ ...options, page })
    allProperties = [...allProperties, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allProperties.length} properties total`)
  return allProperties
}

/**
 * Create Type from API response
 */
export function createType(typeResponse: unknown): Type {
  const apiType = APITypeFromJSON(typeResponse)
  return {
    ...apiType,
    key: getPKfromSelf(apiType.self),
  } as Type
}

/**
 * Fetch paginated types from API
 */
export async function fetchTypes(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Type>> {
  log.info('Fetching types with options:', options)

  const response = await getTypesApi().v1ItemsTypesList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const types = response.results ? response.results.map(createType) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: types,
  }
}

/**
 * Fetch a single type by ID
 */
export async function fetchType(id: string | number): Promise<Type> {
  log.info('Fetching type:', id)

  const response = await getTypesApi().v1ItemsTypesRetrieve({ 
    id: typeof id === 'string' ? parseInt(id, 10) : id 
  })
  return createType(response)
}

/**
 * Fetch all types (recursively fetches all pages)
 */
export async function fetchAllTypes(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Type[]> {
  log.info('Fetching all types')

  let allTypes: Type[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchTypes({ ...options, page })
    allTypes = [...allTypes, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allTypes.length} types total`)
  return allTypes
}

/**
 * Create Family from API response
 */
export function createFamily(familyResponse: unknown): Family {
  const apiFamily = APIFamilyFromJSON(familyResponse)
  return {
    ...apiFamily,
    key: getPKfromSelf(apiFamily.self),
  } as Family
}

/**
 * Fetch paginated families from API
 */
export async function fetchFamilies(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Family>> {
  log.info('Fetching families with options:', options)

  const response = await getFamiliesApi().v1ItemsFamiliesList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const families = response.results ? response.results.map(createFamily) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: families,
  }
}

/**
 * Fetch a single family by ID
 */
export async function fetchFamily(id: string | number): Promise<Family> {
  log.info('Fetching family:', id)

  const response = await getFamiliesApi().v1ItemsFamiliesRetrieve({ 
    id: typeof id === 'string' ? parseInt(id, 10) : id 
  })
  return createFamily(response)
}

/**
 * Fetch all families (recursively fetches all pages)
 */
export async function fetchAllFamilies(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Family[]> {
  log.info('Fetching all families')

  let allFamilies: Family[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchFamilies({ ...options, page })
    allFamilies = [...allFamilies, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allFamilies.length} families total`)
  return allFamilies
}

/**
 * Create Material from API response
 */
export function createMaterial(materialResponse: unknown): Material {
  const apiMaterial = APIMaterialFromJSON(materialResponse)
  return {
    ...apiMaterial,
    key: getPKfromSelf(apiMaterial.self),
  } as Material
}

/**
 * Fetch paginated materials from API
 */
export async function fetchMaterials(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Material>> {
  log.info('Fetching materials with options:', options)

  const response = await getMaterialsApi().v1ItemsMaterialsList({
    page: options.page,
    pageSize: options.pageSize,
  })

  const materials = response.results ? response.results.map(createMaterial) : []

  return {
    count: response.count || 0,
    next: response.next || null,
    previous: response.previous || null,
    results: materials,
  }
}

/**
 * Fetch a single material by ID
 */
export async function fetchMaterial(id: string | number): Promise<Material> {
  log.info('Fetching material:', id)

  const response = await getMaterialsApi().v1ItemsMaterialsRetrieve({ 
    id: typeof id === 'string' ? parseInt(id, 10) : id 
  })
  return createMaterial(response)
}

/**
 * Fetch all materials (recursively fetches all pages)
 */
export async function fetchAllMaterials(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Material[]> {
  log.info('Fetching all materials')

  let allMaterials: Material[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchMaterials({ ...options, page })
    allMaterials = [...allMaterials, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allMaterials.length} materials total`)
  return allMaterials
}

/**
 * Convert form input to API Category JSON
 */
export function formInputToAPICategoryJSON(input: any): { name: string } {
  return {
    ...(input.name && { name: input.name }),
  }
}

/**
 * Convert form input to API Property JSON
 */
export function formInputToAPIPropertyJSON(input: any): { name: string; value?: string; type?: string } {
  return {
    ...(input.name && { name: input.name }),
    ...(input.value && { value: input.value }),
    ...(input.type && { type: input.type }),
  }
}

/**
 * Create a new category
 */
export async function createCategoryApi(categoryData: { name: string }): Promise<Category> {
  log.info('Creating category:', categoryData)

  const response = await getCategoriesApi().v1ItemsCategoriesCreate({
    aPICategoryRequest: categoryData as any,
  })

  return createCategory(response)
}

/**
 * Create a new property
 */
export async function createPropertyApi(propertyData: { name: string; value?: string; type?: string }): Promise<Property> {
  log.info('Creating property:', propertyData)

  const response = await getPropertiesApi().v1ItemsPropertiesCreate({
    aPIPropertyRequest: propertyData as any,
  })

  return createProperty(response)
}

/**
 * Create a category, property, or supplier based on URL
 * This is a unified function that replaces the old handleAddOption callback-based function
 */
export async function createCategoryPropertyOrSupplier(
  url: 'v1/supplier/suppliers/' | 'v1/items/categories/' | 'v1/items/properties/',
  input: any
): Promise<Category | Property | Supplier> {
  log.info('Creating option:', url, input)

  switch (url) {
    case 'v1/supplier/suppliers/': {
      const supplierJson = formInputToAPISupplierJSON(input)
      return await createSupplierApi(supplierJson)
    }
    case 'v1/items/categories/': {
      const categoryJson = formInputToAPICategoryJSON(input)
      return await createCategoryApi(categoryJson)
    }
    case 'v1/items/properties/': {
      const propertyJson = formInputToAPIPropertyJSON(input)
      return await createPropertyApi(propertyJson)
    }
    default:
      throw new Error(
        `Error in createCategoryPropertyOrSupplier! Provided url ${url} does not match any of the valid urls v1/supplier/suppliers/, v1/items/categories/, or v1/items/properties/.`
      )
  }
}
